import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { JsonLd } from "@/components/site/JsonLd";
import { CtaBand } from "@/components/site/Sections";
import { SiteLayout } from "@/components/site/SiteLayout";
import { WhatsAppButton } from "@/components/site/WhatsAppChat";
import { getPublishedPost } from "@/lib/blog.functions";
import { business } from "@/lib/business";
import { breadcrumbSchema, canonicalLink, metaFor } from "@/lib/seo";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const post = await getPublishedPost({ data: { slug: params.slug } });
    if (!post) throw notFound();
    return { post };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Artigo não encontrado | Chavel" }, { name: "robots", content: "noindex" }] };
    }
    const path = `/blog/${params.slug}`;
    return {
      meta: metaFor({
        title: `${loaderData.post.title} | Chavel Chaveiro Betim`,
        description: loaderData.post.excerpt,
        path,
        type: "article",
        image: loaderData.post.image_url ?? undefined,
      }),
      links: canonicalLink(path),
    };
  },
  component: BlogPost,
});

function renderContent(content: string) {
  return content.split(/\n{2,}/).map((block, index) => {
    const text = block.trim();
    if (!text) return null;
    if (text.startsWith("### ")) return <h3 key={index}>{text.slice(4)}</h3>;
    if (text.startsWith("## ")) return <h2 key={index}>{text.slice(3)}</h2>;
    if (text.startsWith("# ")) return <h2 key={index}>{text.slice(2)}</h2>;
    if (/^[-*] /m.test(text)) {
      const items = text.split("\n").filter((line) => /^[-*] /.test(line.trim()));
      return (
        <ul key={index}>
          {items.map((item) => (
            <li key={item}>{item.replace(/^[-*] /, "")}</li>
          ))}
        </ul>
      );
    }
    return <p key={index}>{text}</p>;
  });
}

function BlogPost() {
  const { post } = Route.useLoaderData();
  const url = `/blog/${post.slug}`;

  return (
    <SiteLayout>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: post.image_url ?? undefined,
          datePublished: post.published_at,
          dateModified: post.updated_at,
          keywords: post.target_keyword ?? undefined,
          author: { "@type": "Organization", name: business.name },
          publisher: { "@type": "Organization", name: business.name },
          contentLocation: {
            "@type": "Place",
            name: post.neighborhood ? `${post.neighborhood}, ${post.city} - ${post.region}` : `${post.city} - ${post.region}`,
            geo:
              post.latitude && post.longitude
                ? { "@type": "GeoCoordinates", latitude: post.latitude, longitude: post.longitude }
                : undefined,
          },
          mainEntityOfPage: url,
        }}
      />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Início", url: "/" },
          { name: "Blog", url: "/blog" },
          { name: post.title, url },
        ])}
      />

      <article className="container-page grid gap-10 py-12 lg:grid-cols-[1.6fr_0.9fr]">
        <div>
          <nav aria-label="Trilha de navegação" className="text-xs text-muted-foreground">
            <Link to="/" className="hover:underline">Início</Link>
            {" / "}
            <Link to="/blog" className="hover:underline">Blog</Link>
          </nav>
          <h1 className="mt-3 font-display text-3xl leading-tight md:text-4xl">{post.title}</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {post.neighborhood ? `${post.neighborhood}, ` : ""}
            {post.city} - {post.region}
            {post.published_at ? ` • ${new Date(post.published_at).toLocaleDateString("pt-BR")}` : ""}
          </p>
          {post.image_url ? (
            <img
              src={post.image_url}
              alt={post.image_alt ?? post.title}
              className="mt-6 w-full rounded-xl object-cover"
              loading="lazy"
            />
          ) : null}
          <div className="prose-article mt-6 max-w-none">{renderContent(post.content)}</div>
        </div>

        <aside className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold">Chaveiro em Betim 24 horas</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              A Chavel atende desde 1975 na Av. Amazonas, 1354 - Brasiléia, Betim.
            </p>
            <WhatsAppButton className="mt-4 w-full">Pedir atendimento</WhatsAppButton>
            <a href={`tel:${business.phoneRaw}`} className="mt-3 block text-center text-sm font-semibold text-primary">
              {business.phone}
            </a>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold">Leia também</h2>
            <ul className="mt-3 space-y-2 text-sm">
              <li><Link to="/$slug" params={{ slug: "chaveiro-24-horas-betim" }} className="text-primary underline">Chaveiro 24 horas em Betim</Link></li>
              <li><Link to="/$slug" params={{ slug: "chaveiro-automotivo-betim" }} className="text-primary underline">Chaveiro automotivo em Betim</Link></li>
              <li><Link to="/$slug" params={{ slug: "fechadura-digital" }} className="text-primary underline">Fechadura digital</Link></li>
              <li><Link to="/servicos" className="text-primary underline">Todos os serviços</Link></li>
            </ul>
          </div>
        </aside>
      </article>

      <CtaBand title="Ficou com dúvida sobre o seu caso?" text="Fale com a Chavel e receba orientação antes de contratar." />
    </SiteLayout>
  );
}
